import { withMetrics } from "@expert/cloudwatch-metrics";
import { getLogger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";
import ky from "ky";
import { env } from "@/config";
import { userCache } from "@/shared-utils";

interface GaiaSummaryRequest {
    callSid: string;
    sessionId: string;
    partner: Partner;
}

interface BackupGaiaSummaryRequest {
    sessionId: string;
    partner: Partner;
}

interface GaiaSummaryResponse {
    response: string;
}

interface GaiaHistoryMessage {
    role: string;
    id: string;
    sessionId: string;
    messageId: string;
    content: string;
    timestamp: string;
}

interface GaiaHistoryResponse {
    messages: GaiaHistoryMessage[];
    sessionGroupId: string;
    userId: string;
    inputTimestamp: string;
    outputTimestamp: string;
}

const logger = getLogger({ module: "sessionSummary", supportTeam: "solve" });
type GaiaSummaryModel = string;
const fetchGaiaSummary = async (request: GaiaSummaryRequest, model: string) => {
    return await withMetrics(
        "getGaiaSummary",
        () =>
            ky
                .post(`${env.gaiaApiUrl}/orchestrator/invoke`, {
                    headers: {
                        "x-app-name": model,
                        "x-partner-name": request.partner,
                        "x-correlation-id": request.sessionId,
                        Authorization: `Bearer ${userCache.ssoAccessToken}`,
                        "Content-Type": "application/json",
                    },
                    json: {
                        sessionId: request.callSid,
                        sessionGroupId: request.sessionId,
                        message: crypto.randomUUID(),
                        userId: userCache.employeeId,
                        messageId: request.sessionId,
                        userType: "expert",
                    },
                    timeout: 15000,
                })
                .json<GaiaSummaryResponse>(),
        { logger: logger.child(request), rethrow: false, logResult: false },
    );
};

const fetchGaiaSummaryHistory = async (
    request: GaiaSummaryRequest | BackupGaiaSummaryRequest,
    model: GaiaSummaryModel,
) => {
    return await withMetrics(
        "getGaiaSummaryHistory",
        () =>
            ky
                .get(`${env.gaiaApiUrl}/orchestrator/session-history?sessionGroupId=${request.sessionId}&limit=1`, {
                    headers: {
                        "x-app-name": model,
                        "x-partner-name": request.partner,
                        "x-correlation-id": request.sessionId,
                        Authorization: `Bearer ${userCache.ssoAccessToken}`,
                        "Content-Type": "application/json",
                    },
                    timeout: 5000,
                })
                .json<GaiaHistoryResponse>(),
        { logger: logger.child(request), rethrow: false, logResult: false, failureLogLevel: "warn" },
    );
};

export const getGaiaSummary = async (request: GaiaSummaryRequest, model: string) => {
    logger.debug(request, "SessionSummary | Fetching gaia generated summary");
    const gaiaSummary = await fetchGaiaSummary(request, model);
    if (gaiaSummary) {
        return gaiaSummary.response;
    }
    return await getBackupGaiaSummary({ sessionId: request.sessionId, partner: request.partner }, model);
};

export const getBackupGaiaSummary = async (request: BackupGaiaSummaryRequest, model: string) => {
    logger.debug(request, "SessionSummary | Fetching backup gaia generated summary");
    const gaiaSummaryHistory = await fetchGaiaSummaryHistory(request, model);
    if (gaiaSummaryHistory) {
        return gaiaSummaryHistory.messages.at(0)?.content ?? "";
    }
    return "";
};
